/**
 * Location scenario layouts (S17, direct PoI visit) — client-derived
 * rendering for `LocationScenario.layout.template`, a bare string key with no
 * coordinates of its own (backend/prisma/seed.ts's docstring: "lootObjects
 * carry counts but no coordinates, so object placement would be
 * client-derived"). Same call S16 made for world-map region positions.
 *
 * Only two templates are seeded today (`generic_ruins_small`, `arena_ring`);
 * an unrecognized key falls back to `DEFAULT_TEMPLATE` rather than throwing —
 * a new LocationScenario authored on the backend without a matching frontend
 * entry degrades to a plain room instead of breaking the client.
 */
import type { Vec2, WorldData, WorldObject } from '../../types';
import type { LocationInstanceView } from '../../../state/location-state.types';
import { mapsInCategory } from './registry';

interface LocationLayoutTemplate {
  ground: { width: number; depth: number };
  spawnPoint: Vec2;
  /** Non-interactable atmosphere — rubble, low walls. Never collides with loot placement (see LOOT_ROW_Z). */
  decor: WorldObject[];
}

/** Where the loot row sits, relative to spawn — decor objects below stay clear of this line. */
const LOOT_ROW_Z = -7;
const LOOT_SPACING = 3;

const TEMPLATES: Record<string, LocationLayoutTemplate> = {
  generic_ruins_small: {
    ground: { width: 22, depth: 22 },
    spawnPoint: { x: 0, z: 8 },
    decor: [
      {
        id: 'decor-rubble-1', kind: 'prop', label: 'Rubble', position: { x: -6, z: -2 }, size: { w: 2.4, d: 2, h: 1.1 }, color: 0x4a4238, interactable: false, noClip: false,
      },
      {
        id: 'decor-rubble-2', kind: 'prop', label: 'Rubble', position: { x: 6.5, z: 1 }, size: { w: 1.8, d: 1.6, h: 0.9 }, color: 0x4a4238, interactable: false, noClip: false,
      },
      {
        id: 'decor-wall-1', kind: 'building', label: 'Collapsed wall', position: { x: -8, z: -8 }, size: { w: 3, d: 1.2, h: 3.4 }, color: 0x5c5548, interactable: false, rotationY: 0.5,
      },
      {
        id: 'decor-wall-2', kind: 'building', label: 'Collapsed wall', position: { x: 8, z: -6 }, size: { w: 3, d: 1.2, h: 2.6 }, color: 0x5c5548, interactable: false, rotationY: -0.3,
      },
    ],
  },
  arena_ring: {
    ground: { width: 18, depth: 18 },
    spawnPoint: { x: 0, z: 7 },
    decor: [
      {
        id: 'decor-post-1', kind: 'prop', label: 'Ring post', position: { x: -7, z: 0 }, size: { w: 0.6, d: 0.6, h: 2.2 }, color: 0x2c2620, interactable: false, round: true,
      },
      {
        id: 'decor-post-2', kind: 'prop', label: 'Ring post', position: { x: 7, z: 0 }, size: { w: 0.6, d: 0.6, h: 2.2 }, color: 0x2c2620, interactable: false, round: true,
      },
      {
        id: 'decor-post-3', kind: 'prop', label: 'Ring post', position: { x: 0, z: -7 }, size: { w: 0.6, d: 0.6, h: 2.2 }, color: 0x2c2620, interactable: false, round: true,
      },
    ],
  },
};

const DEFAULT_TEMPLATE_KEY = 'generic_ruins_small';

function placeLootObjects(lootObjects: LocationInstanceView['lootObjects']): WorldObject[] {
  const startX = -((lootObjects.length - 1) * LOOT_SPACING) / 2;
  return lootObjects.map((obj, i) => ({
    id: `loot-${obj.id}`,
    kind: 'loot',
    label: obj.objectKey,
    position: { x: startX + i * LOOT_SPACING, z: LOOT_ROW_Z },
    size: { w: 0.9, d: 0.9, h: 0.8 },
    color: 0x8a6a34,
    interactable: !obj.searched,
    lootObjectId: obj.id,
  }));
}

/** Clear ground a crate wants on every side, metres. */
const CRATE_CLEARANCE = 1.6;

function footprintRadius(o: WorldObject): number {
  const w = o.collisionW ?? o.size.w;
  const d = o.collisionD ?? o.size.d;
  return Math.hypot(w, d) / 2;
}

/**
 * Loot for a real map. The raid maps are authored ground with no slots for
 * crates, so they are dealt onto clear ground in a spiral around the spawn —
 * near enough to find at once, never inside a car or a wall. Deterministic:
 * the same instance always lays out the same way.
 */
function scatterLootObjects(
  lootObjects: LocationInstanceView['lootObjects'],
  base: WorldData,
): WorldObject[] {
  const spawn = base.spawnPoint ?? { x: 0, z: 0 };
  const halfW = base.ground.width / 2 - 2;
  const halfD = base.ground.depth / 2 - 2;
  const blockers = base.objects.filter((o) => !o.noClip);
  const zones = base.collisionZones ?? [];
  const taken: Vec2[] = [];
  const free = (x: number, z: number): boolean => {
    if (Math.abs(x) > halfW || Math.abs(z) > halfD) return false;
    for (const o of blockers) {
      if (Math.hypot(o.position.x - x, o.position.z - z) < footprintRadius(o) + CRATE_CLEARANCE) return false;
    }
    for (const zn of zones) {
      if (Math.hypot(zn.position.x - x, zn.position.z - z) < Math.hypot(zn.size.w, zn.size.d) / 2 + CRATE_CLEARANCE) return false;
    }
    return taken.every((t) => Math.hypot(t.x - x, t.z - z) >= LOOT_SPACING);
  };

  return lootObjects.map((obj, i) => {
    let spot: Vec2 | null = null;
    for (let step = 0; step < 400 && !spot; step += 1) {
      const radius = 4 + step * 0.15;
      const angle = (i * 2.4) + step * 0.5; // golden-ish angle: crates fan out, not stack
      const x = spawn.x + Math.cos(angle) * radius;
      const z = spawn.z + Math.sin(angle) * radius;
      if (free(x, z)) spot = { x, z };
    }
    // A map so full nothing fits is a content problem, but the crate must
    // still exist — the server counts it. Put it beside the spawn.
    const position = spot ?? { x: spawn.x + 2 + i, z: spawn.z };
    taken.push(position);
    return {
      id: `loot-${obj.id}`,
      kind: 'loot' as const,
      label: obj.objectKey,
      position,
      size: { w: 0.9, d: 0.9, h: 0.8 },
      color: 0x8a6a34,
      interactable: !obj.searched,
      lootObjectId: obj.id,
    };
  });
}

/** The authored raid map for a PoI's region, if one exists. */
function raidMapForRegion(region: string | undefined): WorldData | null {
  if (!region) return null;
  return mapsInCategory('raid').find((m) => m.region === region)?.data ?? null;
}

/** Builds a renderable `WorldData` for an active Location visit — pure, no side effects. */
export function buildLocationWorld(instance: LocationInstanceView): WorldData {
  const real = raidMapForRegion(instance.region);
  if (real) {
    // The map's own crate slots (placed, modelled) take the server's loot
    // objects in order; their authored ids mean nothing to the server. Slots
    // beyond the server's count are dropped, objects beyond the slots are
    // scattered onto clear ground.
    const slots = real.objects.filter((o) => o.kind === 'loot');
    const scenery = real.objects.filter((o) => o.kind !== 'loot' && o.kind !== 'npc' && !o.npcId); // rule 10 — no ambient NPCs
    const bound: WorldObject[] = instance.lootObjects.slice(0, slots.length).map((obj, i) => ({
      ...slots[i],
      id: `loot-${obj.id}`,
      lootObjectId: obj.id,
      interactable: !obj.searched,
    }));
    const extras = scatterLootObjects(instance.lootObjects.slice(slots.length), { ...real, objects: [...scenery, ...bound] });
    return {
      ...real,
      cityName: instance.poiKey,
      objects: [...scenery, ...bound, ...extras],
      npcs: {},
      dialog: {},
    };
  }
  const template = TEMPLATES[instance.layout.template] ?? TEMPLATES[DEFAULT_TEMPLATE_KEY];
  return {
    cityName: instance.poiKey,
    ground: template.ground,
    spawnPoint: template.spawnPoint,
    objects: [...template.decor, ...placeLootObjects(instance.lootObjects)],
    npcs: {}, // rule 10 — "No ambient NPCs in location maps"
    dialog: {},
  };
}
